import React, { useEffect, useState, useRef} from 'react'
import * as Sentry from '@sentry/gatsby'
import PropTypes from 'prop-types'
import { StyledSportsContainer } from './styles'
import { KambiScript } from './script'
import {
  KambiLocaleSettings,
} from '../../../utils/constants'
import { EnvConfig } from '../../../config/EnvConfig'
import storageService from '../../../services/storageService'
import { getAuthKambiUser } from '../../../adapters/user'
import { useKambiWapi } from '../../../hooks/useKambiWapi'
import useIntercomMessenger from '../../../hooks/useIntercomMessenger'
import { waitForCondition } from '../../../helpers/delay'
import { useEventTracking } from '../../../context/eventTrackingProvider'
import { useOpenBets } from '../../../context/openBetsProvider'

const WEBSITE_LOCALE = process.env.GATSBY_INTL_LOCALE

export const KambiSportsbook = ({
  pathname,
  updateCustomLocation,
  updateWallet,
}) => {
  if (typeof window === 'undefined') {
    return null // Don't render on the server
  }

  const [isInitialized, setIsInitialized] = useState(false)
  const [isKambiLoaded, setIsKambiLoaded] = useState(false)
  const [isKambiFrameworkLoaded, setIsKambiFrameworkLoaded] = useState(false)
  const isKambiFrameworkLoadedRef = useRef(isKambiFrameworkLoaded);
  const isMountedRef = useRef(false)
  const abortController = useRef(new AbortController())
  const { trackEvent, EVENT_NAMES, EVENT_LOCATIONS } = useEventTracking()
  const { refreshOpenBetsCount } = useOpenBets()

  useKambiWapi()
  
  const { startIntercom } = useIntercomMessenger()

  useEffect(() => {
    isKambiFrameworkLoadedRef.current = isKambiFrameworkLoaded;
  }, [isKambiFrameworkLoaded])

  const cleanupKambi = async () => {
    const disposeKambi = async () => {
      return window?._kbc?.dispose()
    }
  
    // Abort previous actions if any
    abortController.current.abort()
    abortController.current = new AbortController()

    if(window?._kbc?.dispose) {
      return disposeKambi()
      .catch((error) => {
        Sentry.captureException('[DisposeKambiError]: ' + error)
      })
    } else {
      // Condition function example
      const checkCondition = () => {
        return Promise.resolve(
          typeof window?._kbc?.dispose !== 'undefined'
        );
      }
      // Execute the waitForCondition function
      return waitForCondition({
        conditionFn: checkCondition,
        MAX_NUM_OF_RETRIES: 60,
        attemptsLimitExceededReturn: 'Kambi Dispose function still not found.',
        delayDuration: 1000,
        signal: abortController.current.signal,
      }).then(result => {
        if (result) {
          Sentry.captureException('[DisposeKambiError]: ' + result)
        } else {
          disposeKambi()
          .catch((error) => {
            Sentry.captureException('[DisposeKambiError]: ' + error)
          })
        }
      })  
    }  
  }

  const handleKambiNotification = (event) => {
    
    const {
      detail: { name, data },
    } = event

    if (name === 'loginRequested'){
      trackEvent(EVENT_NAMES.LOGIN_STARTED, { location: EVENT_LOCATIONS.BETSLIP });
    }

    if (name === 'routeChange') {
      return updateCustomLocation()
    }

    if (name === 'cashedIn' || name === 'placedBet') {
      refreshOpenBetsCount()
      return updateWallet()
    }

    if (name === 'dataLayerPushed' && data?.event === 'kambi loaded') {
      setIsKambiLoaded(true)
      return
    }

    if (name === 'dataLayerPushed' && data?.event === 'kambi startup') {
      setIsKambiFrameworkLoaded(true)
      return
    }

  }

  useEffect(() => {
    isMountedRef.current = true
    setIsInitialized(true)

    window.addEventListener('kambi-notification', handleKambiNotification)

    return () => {
      setIsInitialized(false)
      isMountedRef.current = false
      cleanupKambi()
        .catch((error) => {
          Sentry.captureException('[CleanupKambiError]: ' + error)
        })
        .finally(() => {
          window.removeEventListener(
            'kambi-notification',
            handleKambiNotification
          )
        })
    }
  }, [])

  useEffect(() => {
    if (!isKambiLoaded) return
    startIntercom()
  }, [isKambiLoaded, startIntercom])

  const renderSportsBook = async () => {
    if (!isMountedRef.current) return
    const { currency, locale, market } = KambiLocaleSettings[WEBSITE_LOCALE]

    const user = storageService.getUser()

    let ticket = ''
    let customerCurrency = currency

    if (user) {
      const {
        data: { token },
      } = await getAuthKambiUser()
      ticket = token
      customerCurrency = user?.wallet?.currency?.short_code
    }
    
    window._kc = {
      currency: customerCurrency,
      locale,
      market,
      playerId: user?.id || '',
      ticket,
      racingMode: 'false',
      environment: EnvConfig.GATSBY_KAMBI_ENV,
    }

    if (isMountedRef.current) {
      const script = document.createElement('script')
      script.src = EnvConfig.GATSBY_KAMBI_BOOTSTRAP
      document.body.appendChild(script)
    }
  }

  useEffect(() => {
    if (!isInitialized) return

    try {
      renderSportsBook()
    } catch (error) {
      Sentry.captureException('[RenderKambiSportsBookError]: ' + error)
    }
  }, [isInitialized])

  if (!isInitialized) {
    return null
  }

  return (
    <StyledSportsContainer>
      <KambiScript pathname={pathname} />
      <div id="KambiBC" />
    </StyledSportsContainer>
  )
}

KambiSportsbook.defaultProps = {
  pathname: '',
  updateCustomLocation: () => {},
  updateWallet: () => {},
}

KambiSportsbook.propTypes = {
  pathname: PropTypes.string,
  updateCustomLocation: PropTypes.func.isRequired,
  updateWallet: PropTypes.func.isRequired,
}
